/**
 * 고객 신청 폼 서비스
 * - /form/:realtorId 에서 사용
 * - Firestore에 customers / service_requests 저장
 */
import { db } from '../firebase/config';
import { collection, addDoc, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { getSiteSettings } from './settingsService';
import { computeDbPrice } from './dbPricingService';

// ───── 상수 ─────
export const SERVICE_CATEGORIES = [
  { key: 'moving', label: '이사', sub: '포장 · 반포장 · 일반', emoji: '🚚' },
  { key: 'cleaning', label: '입주청소', sub: '기본 · 딥클리닝', emoji: '🧹' },
  { key: 'internet_tv', label: '인터넷TV', sub: '통신사 신규/이전', emoji: '📡' },
  { key: 'interior', label: '인테리어', sub: '가성비 · 모던 · 심플', emoji: '🏠' },
  { key: 'aircon', label: '에어컨', sub: '설치·청소·이전', emoji: '❄️' },
  { key: 'water_purifier', label: '정수기', sub: '월 렌탈 상담', emoji: '💧' },
  { key: 'kiosk', label: '키오스크', sub: '매장 도입 상담', emoji: '🖥️' },
];

export const SERVICE_LABELS = SERVICE_CATEGORIES.reduce((acc, s) => {
  acc[s.key] = s.label;
  return acc;
}, {});

// 표준 — site_settings.movingTiers 키와 동일 (normal/semi_pack/pack/van)
export const MOVING_TYPES = [
  { key: 'normal', label: '일반이사' },
  { key: 'semi_pack', label: '반포장이사' },
  { key: 'pack', label: '포장이사' },
  { key: 'van', label: '용달이사' },
];

export const MOVING_TYPE_LABELS = {
  // 표준 키
  normal: '일반이사',
  semi_pack: '반포장이사',
  pack: '포장이사',
  van: '용달이사',
  // 레거시/랜딩/기타 variant 호환
  cargo: '용달이사',
  half_pack: '반포장이사',
  full_pack: '포장이사',
  package: '포장이사',
  semi_package: '반포장이사',
  semi: '반포장이사',
  simple: '일반이사',
  general: '일반이사',
  regular: '일반이사',
  ordinary: '일반이사',
};

// 인터넷 서비스 종류 (단독/TV 포함) — ieumConfig.INTERNET_TYPES (통신사) 와 의미 분리
export const INTERNET_SERVICE_TYPES = [
  { key: 'internet_only', label: '인터넷 단독' },
  { key: 'internet_tv', label: '인터넷 + TV' },
];

// 통신사 — 표준 키 (소문자) + ieumConfig.INTERNET_TYPES 와 통합
export const INTERNET_PROVIDERS = [
  { key: 'kt', label: 'KT' },
  { key: 'skt', label: 'SKT' },
  { key: 'lguplus', label: 'LG U+' },
  { key: 'sk_broadband', label: 'SK브로드밴드' },
  { key: 'other', label: '기타' },
];

export const PREFERRED_INTERNET = [
  { key: 'most_support', label: '지원금 많은 쪽' },
  { key: 'kt', label: 'KT' },
  { key: 'skt', label: 'SKT' },
  { key: 'lguplus', label: 'LG U+' },
  { key: 'sk_broadband', label: 'SK브로드밴드' },
  { key: 'other', label: '기타' },
];

// 통신사 라벨 매핑 (레거시 대문자 키 호환 포함)
export const INTERNET_LABELS = {
  kt: 'KT',
  skt: 'SKT',
  lguplus: 'LG U+',
  sk_broadband: 'SK브로드밴드',
  other: '기타',
  most_support: '지원금 많은 쪽',
  // 레거시 호환
  KT: 'KT',
  SK: 'SKT',
  LG: 'LG U+',
  SKB: 'SK브로드밴드',
};

export const CLEANING_TYPES = [
  { key: 'basic', label: '기본 입주청소' },
  { key: 'deep', label: '딥클리닝' },
  { key: 'both', label: '둘 다' },
];

export const INTERIOR_STYLES = [
  { key: 'custom', label: '직접입력' },
  { key: 'value', label: '가성비' },
  { key: 'modern', label: '모던' },
  { key: 'simple', label: '심플' },
];

export const INTERIOR_STYLE_LABELS = {
  custom: '직접입력',
  value: '가성비',
  modern: '모던',
  simple: '심플',
};

export const AREA_SIZE_LABELS = {
  under_12: '12평 미만',
  between_12_20: '12~20평',
  over_20: '20평 이상',
};

// ───── 유틸 ─────
export const PHONE_REGEX = /^01[016789]\d{7,8}$/;

export const validatePhone = (phone) =>
  PHONE_REGEX.test((phone || '').replace(/\D/g, ''));

export const formatPhone = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

export const parseManualPyeong = (raw) => {
  if (raw == null) return null;
  const n = Number(String(raw).trim());
  if (!Number.isFinite(n) || n <= 0 || n > 999) return null;
  return n;
};

export const deriveAreaSize = (pyeong) => {
  const n = parseManualPyeong(pyeong);
  if (n == null) return null;
  if (n <= 12) return 'under_12';
  if (n <= 20) return 'between_12_20';
  return 'over_20';
};

// ───── realtor 조회 ─────
export const getRealtorById = async (realtorId) => {
  if (!realtorId) return null;

  try {
    // Firestore users 컬렉션에서 realtor 검색
    const q = query(
      collection(db, 'users'),
      where('role', '==', 'realtor'),
    );
    const snap = await getDocs(q);
    const found = snap.docs.find(
      (doc) => doc.id === realtorId || doc.data().login_id === realtorId
    );
    if (found) {
      const data = found.data();
      return {
        id: found.id,
        business_name: data.business_name || null,
        name: data.name || null,
      };
    }
  } catch {
    // Firestore 조회 실패 시 fallback
  }

  // fallback — 어떤 realtorId든 기본값 반환 (QR 링크 유입 대응)
  return {
    id: realtorId,
    business_name: null,
    name: null,
  };
};

// ───── 제출 ─────
/**
 * payload 예시:
 * {
 *   name, phone, moving_date, moving_address, current_address,
 *   area_pyeong_exact, memo,
 *   moving_type, has_elevator_from, has_elevator_to,
 *   internet_type, current_internet, preferred_internet,
 *   cleaning_type, cleaning_date, interior_date, interior_style,
 *   services: [],
 *   source_realtor_id, source_url,
 * }
 */
export const submitApplication = async (payload) => {
  // 1. 필수 검증
  if (!payload.name || !payload.name.trim()) {
    throw new Error('이름을 입력해주세요.');
  }
  if (!validatePhone(payload.phone)) {
    throw new Error('올바른 연락처를 입력해주세요. (010으로 시작하는 10~11자리)');
  }
  if (!Array.isArray(payload.services) || payload.services.length === 0) {
    throw new Error('서비스를 하나 이상 선택해주세요.');
  }
  if (payload.services.includes('moving')) {
    if (!payload.moving_address || !payload.moving_address.trim()) {
      throw new Error('이사할 주소를 입력해주세요.');
    }
    if (parseManualPyeong(payload.area_pyeong_exact) == null) {
      throw new Error('이사 평수를 숫자로 입력해주세요.');
    }
    if (!payload.moving_type) {
      throw new Error('이사 종류를 선택해주세요.');
    }
  }

  const phoneDigits = (payload.phone || '').replace(/\D/g, '');

  // 2. 당일 중복 신청 체크
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const dupQ = query(
    collection(db, 'customers'),
    where('phone', '==', phoneDigits),
    where('created_at', '>=', Timestamp.fromDate(todayStart))
  );
  const dupSnap = await getDocs(dupQ);
  if (!dupSnap.empty) {
    throw new Error('이미 오늘 신청하셨습니다. 배정된 업체에서 곧 연락드릴 예정입니다.');
  }

  // 3. customers 저장
  const pyeongExact = parseManualPyeong(payload.area_pyeong_exact);
  const now = Timestamp.now();

  const customerRef = await addDoc(collection(db, 'customers'), {
    name: payload.name.trim(),
    phone: phoneDigits,
    moving_date: payload.moving_date || null,
    moving_address: payload.moving_address || '',
    current_address: payload.current_address || null,
    area_pyeong_exact: pyeongExact,
    area_size: deriveAreaSize(pyeongExact),
    moving_type: payload.moving_type || null,
    has_elevator_from: payload.has_elevator_from ?? true,
    has_elevator_to: payload.has_elevator_to ?? true,
    internet_type: payload.internet_type || null,
    current_internet_provider: payload.current_internet || null,
    preferred_internet: payload.preferred_internet || null,
    cleaning_type: payload.cleaning_type || null,
    cleaning_date: payload.cleaning_date || null,
    interior_date: payload.interior_date || null,
    interior_style: payload.interior_style || null,
    memo: payload.memo || null,
    source_realtor_id: payload.source_realtor_id || null,
    source_url: payload.source_url || null,
    source_type: 'landing',
    services: payload.services,
    created_at: now,
    updated_at: now,
  });

  // 4. service_requests 저장 (서비스별 1문서)
  // 본사 가격표 기반 열람가 산출하여 price 필드 동시 저장 (제휴업체 결제 단가)
  const settings = await getSiteSettings().catch(() => null);
  const customerForPrice = {
    area_pyeong_exact: pyeongExact,
    area_size: deriveAreaSize(pyeongExact),
  };
  for (const category of payload.services) {
    const memo = category === 'interior' && payload.interior_style === 'custom'
      ? payload.interior_custom || ''
      : '';
    const srForPrice = {
      category,
      moving_type: payload.moving_type || '',
      area_pyeong_exact: pyeongExact,
      area_size: deriveAreaSize(pyeongExact),
    };
    const price = computeDbPrice(settings, srForPrice, customerForPrice);
    await addDoc(collection(db, 'service_requests'), {
      customer_id: customerRef.id,
      category,
      hq_status: 'unread',
      assigned_partner_id: null,
      source_realtor_id: payload.source_realtor_id || null,
      memo,
      price,
      area_pyeong_exact: pyeongExact,
      area_size: deriveAreaSize(pyeongExact),
      ...(category === 'moving' ? { moving_type: payload.moving_type || '' } : {}),
      ...(category === 'internet_tv' ? {
        internet_type: payload.internet_type || '',
        preferred_internet: payload.preferred_internet || '',
      } : {}),
      ...(category === 'cleaning' ? { cleaning_type: payload.cleaning_type || '' } : {}),
      ...(category === 'interior' ? {
        interior_style: payload.interior_style || '',
        interior_custom: payload.interior_custom || '',
      } : {}),
      created_at: now,
      updated_at: now,
    });
  }

  return { success: true, customerId: customerRef.id };
};
